import type {
  AdvanceAnnouncementAction,
  ConfirmRolePeekAction,
  GameState,
  NightActionPayload,
  NightRoleStep,
  RoleId,
  StartNightAction,
  SubmitNightActionAction,
  ValidationResult,
} from "../types";
import { validateHostAction } from "./common";

function isValidCenterSlotIndex(index: number): index is 0 | 1 | 2 {
  return index === 0 || index === 1 || index === 2;
}

function hasPlayer(state: GameState, playerId: string): boolean {
  return state.players.some((player) => player.id === playerId);
}

function nextPendingNightStep(state: GameState): NightRoleStep | undefined {
  return state.nightRoleQueue.find((step) => step.status === "pending");
}

function nextPendingAnnouncement(state: GameState) {
  return state.announcerTimeline.find((step) => step.status === "pending");
}

function validateNightPhase(state: GameState): ValidationResult {
  if (state.phase !== "night") {
    return { valid: false, error: "Action is only allowed during night" };
  }
  return { valid: true };
}

function validateCurrentStep(
  state: GameState,
  roleId: RoleId,
): ValidationResult & { readonly step?: NightRoleStep } {
  const step = nextPendingNightStep(state);
  if (!step) {
    return { valid: false, error: "No pending night step" };
  }
  if (step.roleId !== roleId) {
    return {
      valid: false,
      error: "Role is not the current night step",
      details: { expected: step.roleId, actual: roleId },
    };
  }
  return { valid: true, step };
}

function validateActor(
  step: NightRoleStep,
  actorPlayerId: string,
): ValidationResult {
  if (!step.actorPlayerIds.includes(actorPlayerId)) {
    return {
      valid: false,
      error: "Player is not an actor for the current night step",
    };
  }
  if (step.completedPlayerIds.includes(actorPlayerId)) {
    return {
      valid: false,
      error: "Player already completed this night step",
    };
  }
  return { valid: true };
}

function validateCenterSlot(state: GameState, index: number): ValidationResult {
  if (!isValidCenterSlotIndex(index) || !state.centerSlots[index]) {
    return { valid: false, error: "Center slot is not available" };
  }
  return { valid: true };
}

function validateSeerPayload(
  state: GameState,
  actorPlayerId: string,
  payload: NightActionPayload,
): ValidationResult {
  if (payload.kind === "seerInspectPlayer") {
    if (payload.targetPlayerId === actorPlayerId) {
      return { valid: false, error: "Seer must inspect another player" };
    }
    if (!hasPlayer(state, payload.targetPlayerId)) {
      return { valid: false, error: "Target player does not exist" };
    }
    return { valid: true };
  }

  if (payload.kind === "seerInspectCenter") {
    const [left, right] = payload.centerSlotIndexes;
    if (left === right) {
      return {
        valid: false,
        error: "Seer must inspect two different center cards",
      };
    }
    const leftCheck = validateCenterSlot(state, left);
    if (!leftCheck.valid) return leftCheck;
    return validateCenterSlot(state, right);
  }

  return { valid: false, error: "Invalid action for seer" };
}

function validateRobberPayload(
  state: GameState,
  actorPlayerId: string,
  payload: NightActionPayload,
): ValidationResult {
  if (payload.kind !== "robberSwap") {
    return { valid: false, error: "Invalid action for robber" };
  }
  if (payload.targetPlayerId === actorPlayerId) {
    return { valid: false, error: "Robber must choose another player" };
  }
  if (!hasPlayer(state, payload.targetPlayerId)) {
    return { valid: false, error: "Target player does not exist" };
  }
  return { valid: true };
}

function validateTroublemakerPayload(
  state: GameState,
  actorPlayerId: string,
  payload: NightActionPayload,
): ValidationResult {
  if (payload.kind !== "troublemakerSwap") {
    return { valid: false, error: "Invalid action for troublemaker" };
  }
  const [left, right] = payload.targetPlayerIds;
  if (left === right || left === actorPlayerId || right === actorPlayerId) {
    return {
      valid: false,
      error: "Troublemaker must choose two different other players",
    };
  }
  if (!hasPlayer(state, left) || !hasPlayer(state, right)) {
    return { valid: false, error: "Target player does not exist" };
  }
  return { valid: true };
}

function validateWerewolfPayload(
  state: GameState,
  step: NightRoleStep,
  payload: NightActionPayload,
): ValidationResult {
  if (payload.kind === "confirmNoAction") {
    return { valid: true };
  }
  if (payload.kind !== "werewolfPeekCenter") {
    return { valid: false, error: "Invalid action for werewolf" };
  }
  if (step.actorPlayerIds.length > 1) {
    return {
      valid: false,
      error: "Only a lone werewolf may inspect a center card",
    };
  }
  return validateCenterSlot(state, payload.centerSlotIndex);
}

function validateDrunkPayload(
  state: GameState,
  payload: NightActionPayload,
): ValidationResult {
  if (payload.kind !== "drunkSwapCenter") {
    return { valid: false, error: "Invalid action for drunk" };
  }
  return validateCenterSlot(state, payload.centerSlotIndex);
}

function validateInsomniacPayload(
  payload: NightActionPayload,
): ValidationResult {
  if (payload.kind !== "insomniacInspectOwn") {
    return { valid: false, error: "Invalid action for insomniac" };
  }
  return { valid: true };
}

function validateDoppelgangerPayload(
  state: GameState,
  actorPlayerId: string,
  payload: NightActionPayload,
): ValidationResult {
  const copiedRoleId = state.doppelgangerCopiedRoleId ?? null;

  if (copiedRoleId === null) {
    // confirmNoAction is accepted as a legacy no-op (pre-doppelganger
    // clients): the step completes without copying.
    if (payload.kind === "confirmNoAction") {
      return { valid: true };
    }
    if (payload.kind !== "doppelgangerCopy") {
      return {
        valid: false,
        error: "Doppelganger must copy another player's role first",
      };
    }
    if (payload.targetPlayerId === actorPlayerId) {
      return { valid: false, error: "Doppelganger must copy another player" };
    }
    if (!hasPlayer(state, payload.targetPlayerId)) {
      return { valid: false, error: "Target player does not exist" };
    }
    return { valid: true };
  }

  if (payload.kind === "doppelgangerCopy") {
    return { valid: false, error: "Doppelganger has already copied a role" };
  }

  // Second submit inside the doppelganger step: the copied role's action.
  switch (copiedRoleId) {
    case "seer":
      return validateSeerPayload(state, actorPlayerId, payload);
    case "robber":
      return validateRobberPayload(state, actorPlayerId, payload);
    case "troublemaker":
      return validateTroublemakerPayload(state, actorPlayerId, payload);
    case "drunk":
      return validateDrunkPayload(state, payload);
    default:
      return {
        valid: false,
        error: "Copied role has no immediate doppelganger action",
      };
  }
}

function validatePayload(
  state: GameState,
  step: NightRoleStep,
  action: SubmitNightActionAction,
): ValidationResult {
  switch (action.roleId) {
    case "doppelganger":
      return validateDoppelgangerPayload(
        state,
        action.actorPlayerId,
        action.payload,
      );
    case "werewolf":
      return validateWerewolfPayload(state, step, action.payload);
    case "seer":
      return validateSeerPayload(state, action.actorPlayerId, action.payload);
    case "robber":
      return validateRobberPayload(state, action.actorPlayerId, action.payload);
    case "troublemaker":
      return validateTroublemakerPayload(
        state,
        action.actorPlayerId,
        action.payload,
      );
    case "drunk":
      return validateDrunkPayload(state, action.payload);
    case "insomniac":
      return validateInsomniacPayload(action.payload);
    case "minion":
      // confirmNoAction is accepted as a legacy no-op (pre-minion clients).
      if (
        action.payload.kind === "minionSeeWerewolves" ||
        action.payload.kind === "confirmNoAction"
      ) {
        return { valid: true };
      }
      return { valid: false, error: "Invalid action for minion" };
    case "mason":
      // confirmNoAction is accepted as a legacy no-op (pre-mason clients).
      if (
        action.payload.kind === "masonSeeMasons" ||
        action.payload.kind === "confirmNoAction"
      ) {
        return { valid: true };
      }
      return { valid: false, error: "Invalid action for mason" };
    default:
      if (action.payload.kind === "confirmNoAction") return { valid: true };
      return { valid: false, error: "Invalid action for role" };
  }
}

export function validateStartNight(
  state: GameState,
  action: StartNightAction,
): ValidationResult {
  const hostCheck = validateHostAction(
    state,
    action.actorPlayerId,
    "Only host can start night",
  );
  if (!hostCheck.valid) return hostCheck;

  if (state.phase !== "role-peek") {
    return { valid: false, error: "Night can only start after role peek" };
  }
  if (state.cards.length === 0) {
    return { valid: false, error: "Cards must be dealt before night" };
  }
  if (state.rolePeekConfirmedPlayerIds.length !== state.players.length) {
    return {
      valid: false,
      error: "All players must confirm role peek before night starts",
    };
  }
  return { valid: true };
}

export function validateConfirmRolePeek(
  state: GameState,
  action: ConfirmRolePeekAction,
): ValidationResult {
  const phaseCheck =
    state.phase === "role-peek"
      ? { valid: true as const }
      : {
          valid: false as const,
          error: "Role peek can only be confirmed during role peek",
        };
  if (!phaseCheck.valid) return phaseCheck;

  if (!hasPlayer(state, action.actorPlayerId)) {
    return { valid: false, error: "Player does not exist" };
  }
  if (state.rolePeekConfirmedPlayerIds.includes(action.actorPlayerId)) {
    return {
      valid: false,
      error: "Player already confirmed role peek",
    };
  }
  return { valid: true };
}

export function validateAdvanceAnnouncement(
  state: GameState,
  action: AdvanceAnnouncementAction,
): ValidationResult {
  const hostCheck = validateHostAction(
    state,
    action.actorPlayerId,
    "Only host can advance announcements",
  );
  if (!hostCheck.valid) return hostCheck;

  const phaseCheck = validateNightPhase(state);
  if (!phaseCheck.valid) return phaseCheck;

  const step = nextPendingAnnouncement(state);
  if (!step) {
    return { valid: false, error: "No pending announcement step" };
  }
  if (step.roleId !== action.roleId) {
    return {
      valid: false,
      error: "Role is not the current announcement step",
      details: { expected: step.roleId, actual: action.roleId },
    };
  }
  return { valid: true };
}

export function validateSubmitNightAction(
  state: GameState,
  action: SubmitNightActionAction,
): ValidationResult {
  const phaseCheck = validateNightPhase(state);
  if (!phaseCheck.valid) return phaseCheck;

  const stepCheck = validateCurrentStep(state, action.roleId);
  if (!stepCheck.valid || !stepCheck.step) return stepCheck;

  const actorCheck = validateActor(stepCheck.step, action.actorPlayerId);
  if (!actorCheck.valid) return actorCheck;

  return validatePayload(state, stepCheck.step, action);
}
